/**
 * The estate: tickets, configuration items, captured configuration, and telemetry.
 *
 * Configuration and telemetry are separate screens on purpose. They answer different questions —
 * what a device was *told* to do, and what it actually *did* — and a change can look perfectly
 * correct in one while the other shows it did not work. Merging them into "evidence" would hide
 * exactly the disagreement worth seeing.
 */

import { useState } from 'react';
import type { JSX } from 'react';

import { api } from '../shared/api/client';
import {
  Badge,
  Card,
  Empty,
  ErrorBox,
  Formula,
  PageHeader,
  Spinner,
  Table,
} from '../shared/ui/ui';
import { useAsync } from '../shared/ui/useAsync';

export function Captures(): JSX.Element {
  const [ciId, setCiId] = useState('');
  const [submitted, setSubmitted] = useState('');
  const { data, error, loading } = useAsync(
    () => (submitted === '' ? Promise.resolve([]) : api.snapshots(submitted)),
    [submitted],
  );

  const snapshots = data ?? [];
  const [beforeId, setBeforeId] = useState('');
  const [afterId, setAfterId] = useState('');
  const diff = useAsync(
    () =>
      beforeId === '' || afterId === ''
        ? Promise.resolve(null)
        : api.diff(beforeId, afterId),
    [beforeId, afterId],
  );

  return (
    <>
      <PageHeader
        title="Configuration"
        subtitle="What a device was told to do. A before and after pair brackets the work on a ticket."
      />

      <Card title="Configuration item">
        <form
          className="inline-form"
          onSubmit={(e) => {
            e.preventDefault();
            setSubmitted(ciId.trim());
          }}
        >
          <input
            className="input"
            value={ciId}
            onChange={(e) => { setCiId(e.target.value); }}
            placeholder="configuration item id"
            aria-label="Configuration item id"
          />
          <button className="button" type="submit">
            Show captures
          </button>
        </form>
      </Card>

      <ErrorBox error={error} />

      {submitted !== '' && (
        <Card
          title="Captures"
          subtitle="A capture that failed to parse is still kept — it is the artifact needed to fix the parser, and dropping it would lose evidence mid-incident."
        >
          {loading ? (
            <Spinner />
          ) : (
            <Table
              columns={['Captured', 'Role', 'Source', 'Vendor', 'Parsed', 'Entities', 'Compare']}
              empty="No captures for that configuration item."
            >
              {snapshots.map((s) => (
                <tr key={s.id}>
                  <td>{formatDate(s.captured_at)}</td>
                  <td>
                    <Badge tone={s.bracket_role === 'baseline' ? 'muted' : 'info'}>
                      {s.bracket_role}
                    </Badge>
                  </td>
                  <td>{s.source}</td>
                  <td>
                    {s.vendor ?? '—'}
                    {s.detection.forced && (
                      <>
                        {' '}
                        <Badge tone="muted" title="A vendor was asserted, not detected">
                          forced
                        </Badge>
                      </>
                    )}
                  </td>
                  <td>
                    {s.parsed ? (
                      <Badge tone="ok">yes</Badge>
                    ) : (
                      <Badge tone="warn" title={s.parse_error ?? undefined}>
                        no
                      </Badge>
                    )}
                  </td>
                  <td>
                    <Formula>{summarize(s.summary)}</Formula>
                  </td>
                  <td>
                    <button className="button button--small" onClick={() => { setBeforeId(s.id); }}>
                      before
                    </button>{' '}
                    <button className="button button--small" onClick={() => { setAfterId(s.id); }}>
                      after
                    </button>
                  </td>
                </tr>
              ))}
            </Table>
          )}
        </Card>
      )}

      {beforeId !== '' && afterId !== '' && (
        <Card
          title="What changed"
          subtitle="Not merely that something changed — what it means. That characterization is what makes a diff usable as evidence for closing a ticket."
        >
          <ErrorBox error={diff.error} />
          {diff.loading ? (
            <Spinner />
          ) : diff.data === null ? (
            <Empty>Pick a before and an after capture.</Empty>
          ) : diff.data.has_changes ? (
            <ul className="findings">
              {diff.data.recommendations.map((r, i) => (
                <li key={i}>
                  <Badge tone={r.severity === 'warn' ? 'warn' : 'info'}>{r.severity}</Badge>{' '}
                  {r.text}
                </li>
              ))}
            </ul>
          ) : (
            <Empty>No structural difference between these two captures.</Empty>
          )}
        </Card>
      )}
    </>
  );
}

/** Telemetry windows for one configuration item, and what a comparison says. */
export function Telemetry(): JSX.Element {
  const [ciId, setCiId] = useState('');
  const [submitted, setSubmitted] = useState('');
  const { data, error, loading } = useAsync(
    () => (submitted === '' ? Promise.resolve([]) : api.telemetry(submitted)),
    [submitted],
  );

  const windows = data ?? [];
  const [beforeId, setBeforeId] = useState('');
  const [afterId, setAfterId] = useState('');
  const cmp = useAsync(
    () =>
      beforeId === '' || afterId === ''
        ? Promise.resolve(null)
        : api.compareWindows(beforeId, afterId),
    [beforeId, afterId],
  );

  return (
    <>
      <PageHeader
        title="Telemetry"
        subtitle="What a device actually did. A change can diff perfectly and still not fix the fault — this is the half that notices."
      />

      <Card title="Configuration item">
        <form
          className="inline-form"
          onSubmit={(e) => {
            e.preventDefault();
            setSubmitted(ciId.trim());
          }}
        >
          <input
            className="input"
            value={ciId}
            onChange={(e) => { setCiId(e.target.value); }}
            placeholder="configuration item id"
            aria-label="Configuration item id"
          />
          <button className="button" type="submit">
            Show windows
          </button>
        </form>
      </Card>

      <ErrorBox error={error} />

      {submitted !== '' && (
        <Card title="Windows">
          {loading ? (
            <Spinner />
          ) : (
            <Table
              columns={['Observed', 'Role', 'Lines parsed', 'Usable', 'Compare']}
              empty="No telemetry for that configuration item."
            >
              {windows.map((w) => (
                <tr key={w.id}>
                  <td>
                    {formatDate(w.observed_from)} → {formatDate(w.observed_to)}
                  </td>
                  <td>
                    <Badge tone={w.bracket_role === 'baseline' ? 'muted' : 'info'}>
                      {w.bracket_role}
                    </Badge>
                  </td>
                  <td className="table__num">
                    {w.parsed_lines} / {w.total_lines}
                  </td>
                  <td>
                    {w.too_sparse_to_compare ? (
                      <Badge
                        tone="warn"
                        title="Too little parsed to compare. A sparse window compares equal to any other and would report 'no change' regardless of what happened."
                      >
                        too sparse
                      </Badge>
                    ) : (
                      <Badge tone="ok">yes</Badge>
                    )}
                  </td>
                  <td>
                    <button className="button button--small" onClick={() => { setBeforeId(w.id); }}>
                      before
                    </button>{' '}
                    <button className="button button--small" onClick={() => { setAfterId(w.id); }}>
                      after
                    </button>
                  </td>
                </tr>
              ))}
            </Table>
          )}
        </Card>
      )}

      {beforeId !== '' && afterId !== '' && (
        <Card
          title="Did the behavior change?"
          subtitle="Examined in both directions. A change that stops the reported denies while starting new ones elsewhere has not worked."
        >
          <ErrorBox error={cmp.error} />
          {cmp.loading ? (
            <Spinner />
          ) : cmp.data === null ? (
            <Empty>Pick a before and an after window.</Empty>
          ) : (
            <>
              {cmp.data.contradicts_completion && (
                <div className="errorbox">
                  The evidence argues against closing this ticket.
                </div>
              )}
              <ul className="findings">
                {cmp.data.findings.map((f, i) => (
                  <li key={i}>
                    <Badge
                      tone={
                        f.severity === 'warn'
                          ? 'danger'
                          : f.severity === 'resolved'
                            ? 'ok'
                            : f.severity === 'review'
                              ? 'info'
                              : 'muted'
                      }
                    >
                      {f.severity}
                    </Badge>{' '}
                    {f.text}
                  </li>
                ))}
              </ul>
            </>
          )}
        </Card>
      )}
    </>
  );
}

/** Enrolled Collectors and whether they are still reporting. */
export function Collectors(): JSX.Element {
  const { data, error, loading } = useAsync(() => api.collectors());
  const rows = data ?? [];

  return (
    <>
      <PageHeader
        title="Collectors"
        subtitle="The one component that runs inside a client network. Outbound only — Conduit never opens a connection toward it."
      />

      <ErrorBox error={error} />

      <Card
        title="Enrolled"
        subtitle="A Collector that stops reporting produces no captures, and no captures looks exactly like a stable estate. That is what 'stale' is watching for."
      >
        {loading ? (
          <Spinner />
        ) : (
          <Table
            columns={['Name', 'Last seen', 'Version', 'Client key', 'State']}
            empty="No Collectors enrolled."
          >
            {rows.map((c) => (
              <tr key={c.id}>
                <td className="table__strong">{c.name}</td>
                <td>{c.last_seen_at === null ? 'never' : formatDate(c.last_seen_at)}</td>
                <td>{c.agent_version ?? '—'}</td>
                <td>
                  <Formula>{c.contract_key_fingerprint ?? 'not recorded'}</Formula>
                </td>
                <td>
                  {c.revoked ? (
                    <Badge tone="muted">revoked</Badge>
                  ) : c.is_stale ? (
                    <Badge tone="warn">stale</Badge>
                  ) : (
                    <Badge tone="ok">reporting</Badge>
                  )}
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Card>
    </>
  );
}

function summarize(summary: Record<string, number>): string {
  const parts = Object.entries(summary)
    .filter(([, v]) => v > 0)
    .map(([k, v]) => `${k.replace(/_/g, ' ')} ${String(v)}`);
  return parts.length === 0 ? '—' : parts.join(' · ');
}

function formatDate(iso: string): string {
  // ISO 8601, per the locale conventions in CLAUDE.md — it avoids the US/international ambiguity
  // entirely, which matters on a screen an MSSP shows to clients in several countries.
  return iso.slice(0, 16).replace('T', ' ');
}
